import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@telegram-apps/telegram-ui";
import { EndGameScreen } from "./EndGameScreen";
import { PreGameModal } from "./PreGameModal";
import { TemporaryCoinsService } from "../../services/TemporaryCoinsService"; // Import temporary coins service
import { BubbleSystem, BubbleReward } from "./BubbleSystem";
import {
  HighlightedAreaSystem,
  type HighlightedEffect,
} from "./HighlightedArea";
import { RoundsManager, type RoundState } from "./rounds/RoundsManager";
import { UpgradesEngine } from "./upgrades/engine";
import { getThreeRandomCards } from "./upgrades/cards";
import type { Upgrade } from "./upgrades/types";
import { UpgradePickerModal } from "./UpgradePickerModal";
import { StorageService } from "../../services/LocalStorageService";
// Level system removed
import "./SimpleGame.css";
import {
  createPriceSource,
  type PriceSource,
  type PriceMode,
  type RoundFeedInfo,
} from "./priceSource";
import { PvPBot } from "./pvp/PvPBot";
import { NewsTicker } from "./NewsTicker";

interface StockPoint {
  x: number;
  y: number;
  price: number;
  time: number;
}

interface Trade {
  buyTime: number;
  buyPrice: number;
  sellTime: number;
  sellPrice: number;
}

// Game configuration constants
const GAME_CONFIG = {
  initialBuys: 7, // Number of buys available at game start
  gameDuration: 30, // Game duration in seconds
} as const;

// Stock chart configuration
const chartConfig = {
  startPrice: 100,
  volatility: 0.02, // 2% volatility
  trendStrength: 0.0005, // Slight upward trend
  pointsPerSecond: 7, // How many data points per second (reduced for wider spacing)
  animationSpeed: 45, // Relative to 60 = 1x. Lower to slow down (e.g., 45 ≈ 0.75x)
  visibleTimeSeconds: 15, // Show 15 seconds of data (reduced from 30)
};

function formatDateTimeFriendly(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function SimpleGame() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const [currentPrice, setCurrentPrice] = useState(100);
  const [priceMode, setPriceMode] = useState<PriceMode>("random");
  const priceSourceRef = useRef<PriceSource | null>(null);
  const roundFeedInfoRef = useRef<RoundFeedInfo | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Timer state
  const [timeLeft, setTimeLeft] = useState<number>(GAME_CONFIG.gameDuration); // Game duration
  const [gameEnded, setGameEnded] = useState(false);
  const [gameStarted, setGameStarted] = useState(false); // Track if game has started
  const [initialMoney, setInitialMoney] = useState(1000); // Store initial money for end game calculation

  // Trading state
  const [money, setMoney] = useState(1000); // Starting money
  const [coinCount, setCoinCount] = useState(0); // Number of coins held
  const [isHolding, setIsHolding] = useState(false); // Whether user is holding a position
  const [buyPrice, setBuyPrice] = useState(0); // Price at which user bought
  const [buysLeft, setBuysLeft] = useState<number>(GAME_CONFIG.initialBuys); // Number of buys remaining
  const [purchasePoint, setPurchasePoint] = useState<{
    x: number;
    y: number;
    time: number; // Track the time when purchase was made
  } | null>(null); // Purchase coordinates

  // Bubble system state
  const bubbleSystemRef = useRef<BubbleSystem>(new BubbleSystem());
  const highlightedAreaSystemRef = useRef<HighlightedAreaSystem>(
    new HighlightedAreaSystem()
  );
  const [rewardAnimations, setRewardAnimations] = useState<
    {
      id: string;
      x: number;
      y: number;
      value: number;
      startTime: number;
    }[]
  >([]);
  const rewardAnimationsRef = useRef(rewardAnimations);
  const suppressTradeRef = useRef(false);
  const lastTouchTimeRef = useRef(0);
  const IGNORE_MOUSE_AFTER_TOUCH_MS = 800;

  // Precision mechanics removed

  // Session overview state/data
  const [showSessionOverview, setShowSessionOverview] = useState(false);
  const fullDataPointsRef = useRef<StockPoint[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const tradesRef = useRef<Trade[]>([]);
  const currentTimeRef = useRef(0);
  const showOverviewRef = useRef(false);
  const visibleDataPointsRef = useRef<StockPoint[]>([]);

  // Level system removed
  const [isPaused, setIsPaused] = useState(false);

  // PvP bot state
  const botRef = useRef<PvPBot | null>(null);
  const [botMoney, setBotMoney] = useState<number>(0);
  const [botCoinCount, setBotCoinCount] = useState<number>(0);
  const [botIsHolding, setBotIsHolding] = useState<boolean>(false);
  const [botBuyPrice, setBotBuyPrice] = useState<number>(0);
  const [botBuysLeft, setBotBuysLeft] = useState<number>(
    GAME_CONFIG.initialBuys
  );
  const [botPurchasePoint, setBotPurchasePoint] = useState<{
    x: number;
    y: number;
    time: number;
  } | null>(null);
  const botMoneyRef = useRef(botMoney);
  const botCoinCountRef = useRef(botCoinCount);
  const botIsHoldingRef = useRef(botIsHolding);
  const botBuyPriceRef = useRef(botBuyPrice);
  const botBuysLeftRef = useRef(botBuysLeft);
  const botPurchasePointRef = useRef(botPurchasePoint);
  const botLeverageMultRef = useRef<number>(1);
  const botAutoLiqPctRef = useRef<number | undefined>(undefined);
  const botUpgradesRef = useRef<UpgradesEngine>(new UpgradesEngine());

  // Rounds system
  const roundsManagerRef = useRef<RoundsManager>(new RoundsManager());
  const upgradesEngineRef = useRef<UpgradesEngine>(new UpgradesEngine());
  const [roundState, setRoundState] = useState<RoundState | null>(null);
  const roundNumberRef = useRef<number>(1);
  const [roundPassed, setRoundPassed] = useState(false);
  const [roundRemainingDisplay, setRoundRemainingDisplay] = useState<number>(0);
  const roundRemainingTargetRef = useRef<number>(0);
  const roundAnimFrameRef = useRef<number | null>(null);
  const [roundFlash, setRoundFlash] = useState<"gain" | "loss" | null>(null);

  // Gems earned in this session (from new daily max rounds)
  const [sessionGemsEarned, setSessionGemsEarned] = useState<number>(0);

  // Upgrades selection state (pre-game or between rounds future)
  const [showUpgradePicker, setShowUpgradePicker] = useState(false);
  const [offeredUpgrades, setOfferedUpgrades] = useState<
    ReadonlyArray<Upgrade>
  >([]);
  const selectedUpgradesRef = useRef<Upgrade[]>([]);
  const leverageMultRef = useRef<number>(1);
  const autoLiqPctRef = useRef<number | undefined>(undefined);

  // Refs to access current state values in animation loop and timer
  const isHoldingRef = useRef(isHolding);
  const buyPriceRef = useRef(buyPrice);
  const purchasePointRef = useRef(purchasePoint);
  const coinCountRef = useRef(coinCount);
  const currentPriceRef = useRef(currentPrice);
  const moneyRef = useRef(money);
  const buysLeftRef = useRef(buysLeft);
  const isPausedRef = useRef(isPaused);
  // Level system removed

  // Update refs when state changes
  useEffect(() => {
    isHoldingRef.current = isHolding;
    buyPriceRef.current = buyPrice;
    purchasePointRef.current = purchasePoint;
    coinCountRef.current = coinCount;
    currentPriceRef.current = currentPrice;
    moneyRef.current = money;
    buysLeftRef.current = buysLeft;
    isPausedRef.current = isPaused;
    rewardAnimationsRef.current = rewardAnimations;
    tradesRef.current = trades;
    showOverviewRef.current = showSessionOverview;
    // PvP bot refs
    botMoneyRef.current = botMoney;
    botCoinCountRef.current = botCoinCount;
    botIsHoldingRef.current = botIsHolding;
    botBuyPriceRef.current = botBuyPrice;
    botBuysLeftRef.current = botBuysLeft;
    botPurchasePointRef.current = botPurchasePoint;
  }, [
    isHolding,
    buyPrice,
    purchasePoint,
    coinCount,
    currentPrice,
    money,
    buysLeft,
    isPaused,
    rewardAnimations,
    trades,
    showSessionOverview,
    botMoney,
    botCoinCount,
    botIsHolding,
    botBuyPrice,
    botBuysLeft,
    botPurchasePoint,
  ]);

  // Animate round remaining display towards target for visual counting effect
  const animateRoundRemaining = useCallback((from: number, to: number) => {
    if (roundAnimFrameRef.current)
      cancelAnimationFrame(roundAnimFrameRef.current);
    const durationMs = 600;
    const start = performance.now();
    function step(now: number) {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = Math.round(from + (to - from) * eased);
      setRoundRemainingDisplay(value);
      if (t < 1) {
        roundAnimFrameRef.current = requestAnimationFrame(step);
      } else {
        roundAnimFrameRef.current = null;
      }
    }
    roundAnimFrameRef.current = requestAnimationFrame(step);
  }, []);

  const generateNextPrice = useCallback((prev: number) => {
    const src = priceSourceRef.current;
    if (!src) return prev || chartConfig.startPrice;
    return src.next(prev, currentTimeRef.current);
  }, []);

  const drawChart = useCallback(
    (
      canvas: HTMLCanvasElement,
      dataPoints: StockPoint[],
      isHoldingPosition: boolean,
      currentPriceValue: number,
      buyPriceValue: number,
      purchasePointData: { x: number; y: number; time: number } | null,
      botState?: {
        isHolding: boolean;
        buyPrice: number;
        purchasePoint: { x: number; y: number; time: number } | null;
      }
    ) => {
      const ctx = canvas.getContext("2d");
      if (!ctx || dataPoints.length < 2) return;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Set up canvas
      const padding = 60;
      const chartWidth = canvas.width - padding * 2;
      const chartHeight = canvas.height - padding * 2;

      // Calculate price range for scaling
      const prices = dataPoints.map((point) => point.price);
      const minPrice = Math.min(...prices) * 0.95; // Add 5% padding
      const maxPrice = Math.max(...prices) * 1.05;
      const priceRange = maxPrice - minPrice;

      // Draw background
      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw grid lines
      ctx.strokeStyle = "#333";
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);

      // Horizontal grid lines (price levels)
      for (let i = 0; i <= 5; i++) {
        const y = padding + (chartHeight / 5) * i;
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(canvas.width - padding, y);
        ctx.stroke();

        // Price labels
        const price = maxPrice - (priceRange / 5) * i;
        ctx.fillStyle = "#888";
        ctx.font = "12px monospace";
        ctx.textAlign = "right";
        ctx.fillText(`$${price.toFixed(2)}`, padding - 10, y + 4);
      }

      // Vertical grid lines (time) - more lines for better granularity
      for (let i = 0; i <= 10; i++) {
        const x = padding + (chartWidth / 10) * i;
        ctx.beginPath();
        ctx.moveTo(x, padding);
        ctx.lineTo(x, canvas.height - padding);
        ctx.stroke();

        // Time labels (show seconds ago)
        if (i % 2 === 0) {
          // Show every other label to avoid crowding
          const secondsAgo = Math.round(
            (chartConfig.visibleTimeSeconds / 10) * (10 - i)
          );
          ctx.fillStyle = "#666";
          ctx.font = "10px monospace";
          ctx.textAlign = "center";
          ctx.fillText(`-${secondsAgo}s`, x, canvas.height - padding + 15);
        }
      }

      ctx.setLineDash([]);

      // Draw axes
      ctx.strokeStyle = "#555";
      ctx.lineWidth = 2;
      ctx.beginPath();
      // Y-axis
      ctx.moveTo(padding, padding);
      ctx.lineTo(padding, canvas.height - padding);
      // X-axis
      ctx.moveTo(padding, canvas.height - padding);
      ctx.lineTo(canvas.width - padding, canvas.height - padding);
      ctx.stroke();

      // Draw stock price line
      if (dataPoints.length > 1) {
        // Color based on trading state: yellow by default, green/red when holding
        let chartColor = "#ffdd44"; // Default yellow
        if (isHoldingPosition && buyPriceValue > 0) {
          chartColor =
            currentPriceValue >= buyPriceValue ? "#00ff88" : "#ff4444";
        }

        ctx.strokeStyle = chartColor;
        ctx.lineWidth = 3;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        ctx.beginPath();
        dataPoints.forEach((point, index) => {
          const x = padding + (point.x / 100) * chartWidth;
          const y =
            padding +
            chartHeight -
            ((point.price - minPrice) / priceRange) * chartHeight;

          if (index === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });
        ctx.stroke();

        // Draw current price indicator
        const lastPoint = dataPoints[dataPoints.length - 1];
        const lastX = padding + (lastPoint.x / 100) * chartWidth;
        const lastY =
          padding +
          chartHeight -
          ((lastPoint.price - minPrice) / priceRange) * chartHeight;

        ctx.fillStyle = ctx.strokeStyle;
        ctx.beginPath();
        ctx.arc(lastX, lastY, 6, 0, Math.PI * 2);
        ctx.fill();

        // Draw purchase line if holding position
        if (
          isHoldingPosition &&
          purchasePointData &&
          buyPriceValue > 0 &&
          purchasePointData.time !== -1
        ) {
          // Calculate purchase point coordinates
          const purchaseY =
            padding +
            chartHeight -
            ((buyPriceValue - minPrice) / priceRange) * chartHeight;

          let purchaseX = lastX; // Default to current position

          // Determine if the graph is scrolling or static
          const maxDataPoints =
            chartConfig.pointsPerSecond * chartConfig.visibleTimeSeconds;
          const isGraphScrolling = dataPoints.length >= maxDataPoints;

          if (isGraphScrolling) {
            // Graph is scrolling - use time-based positioning
            if (dataPoints.length > 0) {
              const latestTime = dataPoints[dataPoints.length - 1].time;
              const oldestTime = dataPoints[0].time;
              const timeRange = latestTime - oldestTime;

              // Calculate where the purchase time falls relative to the current time window
              if (timeRange > 0) {
                const timeSincePurchase = latestTime - purchasePointData.time;
                const timeProgress = timeSincePurchase / timeRange;

                // Calculate x position (can be outside visible area if timeProgress > 1)
                purchaseX = padding + chartWidth - timeProgress * chartWidth;
              }
            }
          } else {
            // Graph is static - use stored x position
            purchaseX = padding + (purchasePointData.x / 100) * chartWidth;
          }

          // Draw the line from purchase point to current point (even if purchase point is off-screen)
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]); // Dashed line
          ctx.beginPath();
          ctx.moveTo(purchaseX, purchaseY);
          ctx.lineTo(lastX, lastY);
          ctx.stroke();
          ctx.setLineDash([]); // Reset line dash

          // Draw purchase point indicator (only if it's within reasonable bounds)
          if (purchaseX > -20 && purchaseX < canvas.width + 20) {
            ctx.fillStyle = "#ffffff";
            ctx.beginPath();
            ctx.arc(purchaseX, purchaseY, 4, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        // PvP bot dotted hold line
        if (
          botState?.isHolding &&
          botState.purchasePoint &&
          botState.buyPrice > 0 &&
          botState.purchasePoint.time !== -1
        ) {
          const purchaseYBot =
            padding +
            chartHeight -
            ((botState.buyPrice - minPrice) / priceRange) * chartHeight;
          let purchaseXBot = lastX;
          const maxDataPoints =
            chartConfig.pointsPerSecond * chartConfig.visibleTimeSeconds;
          const isGraphScrolling = dataPoints.length >= maxDataPoints;
          if (isGraphScrolling) {
            const latestTime = dataPoints[dataPoints.length - 1].time;
            const oldestTime = dataPoints[0].time;
            const timeRange = latestTime - oldestTime;
            if (timeRange > 0) {
              const timeSincePurchase =
                latestTime - botState.purchasePoint.time;
              const timeProgress = timeSincePurchase / timeRange;
              purchaseXBot = padding + chartWidth - timeProgress * chartWidth;
            }
          } else {
            purchaseXBot =
              padding + (botState.purchasePoint.x / 100) * chartWidth;
          }
          const isProfitBot = currentPriceValue >= botState.buyPrice;
          const colorBot = isProfitBot ? "#00ff88" : "#ff4444";
          ctx.strokeStyle = colorBot;
          ctx.lineWidth = 2;
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.moveTo(purchaseXBot, purchaseYBot);
          ctx.lineTo(lastX, lastY);
          ctx.stroke();
          ctx.setLineDash([]);
          if (purchaseXBot > -20 && purchaseXBot < canvas.width + 20) {
            ctx.fillStyle = colorBot;
            ctx.beginPath();
            ctx.arc(purchaseXBot, purchaseYBot, 3, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        // Current price label
        ctx.fillStyle = "#fff";
        ctx.font = "bold 16px monospace";
        ctx.textAlign = "left";
        ctx.fillRect(lastX + 10, lastY - 12, 80, 24);
        ctx.fillStyle = "#000";
        ctx.fillText(`$${lastPoint.price.toFixed(2)}`, lastX + 15, lastY + 4);
      }

      // Removed canvas captions (title and coin label) to avoid overlay issues on mobile
    },
    []
  );

  // Bubble reward handling
  const handleBubbleRewards = useCallback(
    (rewards: BubbleReward[]) => {
      rewards.forEach((reward) => {
        // Add reward appropriately based on current trading state
        if (isHoldingRef.current) {
          // If holding position, convert reward to additional coins at current price
          const additionalCoins = reward.value / currentPriceRef.current;
          setCoinCount((prev) => prev + additionalCoins);
        } else {
          // If not holding, add directly to money
          setMoney((prev) => {
            const next = prev + reward.value;
            // Rounds: update remaining only when realized (not holding)
            if (roundState) {
              const target = roundsManagerRef.current.getRemainingAmount(next);
              const from = roundRemainingDisplay;
              roundRemainingTargetRef.current = target;
              animateRoundRemaining(from, target);
              if (reward.value > 0) {
                setRoundFlash("gain");
                setTimeout(() => setRoundFlash(null), 500);
              }
            }
            return next;
          });
        }

        // Create reward animation
        const animationId = `reward_${Date.now()}_${Math.random()}`;
        setRewardAnimations((prev) => [
          ...prev,
          {
            id: animationId,
            x: reward.x,
            y: reward.y,
            value: reward.value,
            startTime: performance.now(),
          },
        ]);

        // Remove animation after duration
        setTimeout(() => {
          setRewardAnimations((prev) =>
            prev.filter((anim) => anim.id !== animationId)
          );
        }, 2000); // 2 second animation duration
      });
    },
    [roundState, animateRoundRemaining, roundRemainingDisplay]
  );

  const applyHighlightedEffect = useCallback(
    (effect: HighlightedEffect) => {
      switch (effect.type) {
        case "EXTRA_BUYS": {
          setBuysLeft((prev) => prev + Math.max(0, Math.floor(effect.value)));
          break;
        }
        case "BONUS_STARTING_MONEY": {
          const bonus = initialMoney * effect.value; // e.g., +5% of starting money
          if (isHoldingRef.current) {
            // Convert to coins if holding
            setCoinCount((prev) => prev + bonus / currentPriceRef.current);
          } else {
            setMoney((prev) => prev + bonus);
          }
          break;
        }
        case "BONUS_TIME": {
          setTimeLeft((prev) => prev + Math.max(0, Math.floor(effect.value)));
          break;
        }
        case "TAX_CURRENT_SUM": {
          const taxRate = Math.abs(effect.value); // 0.1 → 10%
          if (isHoldingRef.current) {
            // Reduce holdings value by reducing coins proportionally
            setCoinCount((prev) => Math.max(0, prev * (1 - taxRate)));
          } else {
            setMoney((prev) => Math.max(0, prev * (1 - taxRate)));
          }
          break;
        }
        default:
          break;
      }
    },
    [initialMoney]
  );

  const handleBuy = useCallback(() => {
    if (!isHolding && money > 0 && buysLeft > 0) {
      const coinsCanBuy = money / currentPrice;
      setCoinCount(coinsCanBuy);
      setBuyPrice(currentPrice);
      setIsHolding(true);
      setBuysLeft((prev) => prev - 1);

      // Store purchase point for line drawing (will be set properly in animation loop)
      setPurchasePoint({ x: 0, y: 0, time: -1 }); // Placeholder, will be updated in next frame

      // Precision mechanics removed

      // Highlighted area effect on BUY
      const effect = highlightedAreaSystemRef.current.consumeEffectIfTriggered(
        currentTimeRef.current
      );
      if (effect) applyHighlightedEffect(effect);

      // Upgrades: trade_open
      upgradesEngineRef.current.onTradeOpen();
      leverageMultRef.current =
        upgradesEngineRef.current.getCurrentTradeLeverageMult();
      autoLiqPctRef.current =
        upgradesEngineRef.current.getCurrentTradeAutoLiqPct();
    }
  }, [isHolding, money, currentPrice, buysLeft, applyHighlightedEffect]);

  const handleSell = useCallback(() => {
    if (isHolding && coinCount > 0) {
      const sellValue = coinCount * currentPrice;
      const originalInvestment = coinCount * buyPrice;
      const basePct =
        ((currentPrice - buyPrice) / Math.max(1e-9, buyPrice)) *
        100 *
        (leverageMultRef.current || 1);
      const holdMs = Math.max(
        0,
        currentTimeRef.current -
          (purchasePointRef.current?.time ?? currentTimeRef.current)
      );
      const isWin = basePct >= 0;
      const input = {
        baseProfitPct: isWin ? basePct : undefined,
        baseLossPct: !isWin ? Math.abs(basePct) : undefined,
        holdMs,
        currentFees: { buy: 0, sell: 0 },
      } as const;
      const out = isWin
        ? upgradesEngineRef.current.onTradeCloseWin(input)
        : upgradesEngineRef.current.onTradeCloseLoss(input);
      const effectivePct = isWin
        ? out.profitPct ?? basePct
        : -1 * (out.lossPct ?? Math.abs(basePct));
      const pnlDollar = (effectivePct / 100) * originalInvestment;
      const feeBuy = out.fees?.buy ?? 0;
      const feeSell = out.fees?.sell ?? 0;
      const feeDollar = feeBuy * originalInvestment + feeSell * sellValue;

      const previousMoney = moneyRef.current;
      const newMoney = Math.max(0, previousMoney + pnlDollar - feeDollar);
      setMoney(newMoney);
      setCoinCount(0);
      setBuyPrice(0);
      setIsHolding(false);
      setPurchasePoint(null); // Clear purchase point

      // Record trade for overview
      const buyTime = purchasePointRef.current?.time ?? currentTimeRef.current;
      const trade: Trade = {
        buyTime,
        buyPrice: buyPriceRef.current,
        sellTime: currentTimeRef.current,
        sellPrice: currentPrice,
      };
      setTrades((prev) => {
        const next = [...prev, trade];
        tradesRef.current = next;
        return next;
      });

      // Precision mechanics removed

      // Highlighted area effect on SELL
      const effect = highlightedAreaSystemRef.current.consumeEffectIfTriggered(
        currentTimeRef.current
      );
      if (effect) applyHighlightedEffect(effect);

      // Rounds progress update (realized PnL only)
      if (roundState) {
        const target = roundsManagerRef.current.getRemainingAmount(newMoney);
        const from = roundRemainingDisplay;
        roundRemainingTargetRef.current = target;
        animateRoundRemaining(from, target);
        if (newMoney - previousMoney > 0) {
          setRoundFlash("gain");
          setTimeout(() => setRoundFlash(null), 500);
        } else if (newMoney - previousMoney < 0) {
          setRoundFlash("loss");
          setTimeout(() => setRoundFlash(null), 500);
        }
      }
    }
  }, [
    isHolding,
    coinCount,
    currentPrice,
    buyPrice,
    applyHighlightedEffect,
    roundState,
    animateRoundRemaining,
    roundRemainingDisplay,
  ]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      if (showSessionOverview) return;

      // Ignore emulated mouse events that fire after touch on mobile
      if (Date.now() - lastTouchTimeRef.current < IGNORE_MOUSE_AFTER_TOUCH_MS) {
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Try to collect bubble first; if collected, suppress trade for this interaction
      const rewards = bubbleSystemRef.current.collectBubbleAtPoint(x, y);
      if (rewards.length > 0) {
        suppressTradeRef.current = true;
        handleBubbleRewards(rewards);
        return;
      }

      handleBuy();
    },
    [handleBuy, handleBubbleRewards, showSessionOverview]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (showSessionOverview) return;
      // Ignore emulated mouse events that fire after touch on mobile
      if (Date.now() - lastTouchTimeRef.current < IGNORE_MOUSE_AFTER_TOUCH_MS) {
        return;
      }
      if (suppressTradeRef.current) {
        suppressTradeRef.current = false;
        return;
      }
      handleSell();
    },
    [handleSell, showSessionOverview]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();

      if (showSessionOverview) return;

      // Mark the time of the touch to suppress subsequent emulated mouse events
      lastTouchTimeRef.current = Date.now();

      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      if (!touch) return;
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      // Try to collect bubble first; if collected, suppress trade for this interaction
      const rewards = bubbleSystemRef.current.collectBubbleAtPoint(x, y);
      if (rewards.length > 0) {
        suppressTradeRef.current = true;
        handleBubbleRewards(rewards);
        return;
      }

      handleBuy();
    },
    [handleBuy, handleBubbleRewards, showSessionOverview]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      if (showSessionOverview) return;
      // Refresh the touch timestamp at end as well
      lastTouchTimeRef.current = Date.now();
      if (suppressTradeRef.current) {
        suppressTradeRef.current = false;
        return;
      }
      handleSell();
    },
    [handleSell, showSessionOverview]
  );

  // moved below, after handleGameEnd is defined

  // defined after convertFinalMoneyToCoins

  const handlePlayAgain = useCallback(() => {
    // Reset all game state and show pre-game modal again
    setSessionGemsEarned(0);
    setCoinCount(0);
    setIsHolding(false);
    setBuyPrice(0);
    setBuysLeft(GAME_CONFIG.initialBuys);
    setPurchasePoint(null);
    setCurrentPrice(100);
    setTimeLeft(GAME_CONFIG.gameDuration);
    setGameEnded(false);
    setGameStarted(false);
    setIsPlaying(false);
    setIsPaused(false);
    setShowSessionOverview(false);

    // Clear bubble system and reward animations
    bubbleSystemRef.current.clear();
    setRewardAnimations([]);

    // Level system removed

    // Reset session overview data
    fullDataPointsRef.current = [];
    visibleDataPointsRef.current = [];
    setTrades([]);
    tradesRef.current = [];
    currentTimeRef.current = 0;

    // Reset highlighted area system
    highlightedAreaSystemRef.current = new HighlightedAreaSystem();
  }, []);

  const handleStartGame = useCallback(
    async (startingAmount: number, mode: PriceMode) => {
      try {
        setPriceMode(mode);
        // TEMPORARY: Use local storage coins for prototyping
        const currentCoins = TemporaryCoinsService.getCoins();

        // Check if user has enough temporary coins
        if (currentCoins < startingAmount) {
          // TODO: Show error message about insufficient coins
          console.warn(
            `Insufficient coins: need ${startingAmount}, have ${currentCoins}`
          );
          return;
        }

        // Subtract coins from temporary balance
        const success = TemporaryCoinsService.subtractCoins(startingAmount);
        if (!success) {
          console.error("Failed to subtract coins from temporary balance");
          return;
        }

        // Initialize game with selected starting amount
        setMoney(startingAmount);
        setInitialMoney(startingAmount);
        // PvP: mirror starting cash
        setBotMoney(startingAmount);
        setSessionGemsEarned(0);
        setBuysLeft(GAME_CONFIG.initialBuys);
        setBotBuysLeft(GAME_CONFIG.initialBuys);
        setShowSessionOverview(false);
        fullDataPointsRef.current = [];
        setTrades([]);
        tradesRef.current = [];
        currentTimeRef.current = 0;

        // Set initial money for bubble system
        bubbleSystemRef.current.setInitialMoney(startingAmount);

        // Level system removed
        setIsPaused(false);

        // Reset highlighted area system
        highlightedAreaSystemRef.current = new HighlightedAreaSystem();

        // Initialize price source for the upcoming round
        priceSourceRef.current = createPriceSource(mode, {
          chartConfig,
          approximateNeededPoints:
            chartConfig.pointsPerSecond * chartConfig.visibleTimeSeconds + 60,
        });
        roundFeedInfoRef.current = priceSourceRef.current.getInfo();

        // PvP init
        if (mode === "pvp") {
          if (!botRef.current) botRef.current = new PvPBot();
          botRef.current.reset();
          botUpgradesRef.current = new UpgradesEngine();
        }

        // Defer round start until upgrade is picked
        setGameStarted(true);
        setIsPlaying(false);
        selectedUpgradesRef.current = [];
        setOfferedUpgrades(getThreeRandomCards());
        setShowUpgradePicker(true);

        // Precision mechanics removed

        /* BACKEND INTEGRATION: Will be restored in future
      // Get current user data to verify coin balance
      const userData = userService?.getUser();
      if (!userData) {
        throw new Error("User data not available");
      }

      // Check if user has enough coins
      if (userData.balance < startingAmount) {
        // TODO: Show error message about insufficient coins
        return;
      }

      // Update user's coin balance in the game
      await userService?.loadUser();
      */
      } catch (error) {
        console.error("Failed to start game:", error);
        // TODO: Show error message to user
      }
    },
    [
      /* userService */
    ]
  ); // Commented out userService dependency for temporary implementation

  const handleCancelPreGame = useCallback(() => {
    // Exit to menu without starting game
    navigate("/menu");
  }, [navigate]);

  // Level system removed

  // Helper function to convert final money back to temporary coins
  const convertFinalMoneyToCoins = useCallback(
    (finalMoney: number) => {
      // TEMPORARY: Convert all final dollars back to temporary coins
      TemporaryCoinsService.addCoins(finalMoney);
      const profit = finalMoney - initialMoney;
      console.log(
        `Game ended: Final money ${finalMoney}, Initial money ${initialMoney}, Net change: ${
          profit >= 0 ? "+" : ""
        }${profit} coins`
      );
    },
    [initialMoney]
  );

  const handleProceedToEnd = useCallback(() => {
    setShowSessionOverview(false);
    convertFinalMoneyToCoins(moneyRef.current);
    setGameEnded(true);
  }, [convertFinalMoneyToCoins]);

  const handleProceedToNextRound = useCallback(() => {
    // Prepare next round with current money
    const currentCash = moneyRef.current;
    // Reset gameplay state except balance
    setCoinCount(0);
    setIsHolding(false);
    setBuyPrice(0);
    setBuysLeft(GAME_CONFIG.initialBuys);
    setPurchasePoint(null);
    setCurrentPrice(100);
    setTimeLeft(GAME_CONFIG.gameDuration);
    setIsPaused(false);
    setShowSessionOverview(false);

    // Clear rendering/aux systems
    bubbleSystemRef.current.clear();
    setRewardAnimations([]);
    fullDataPointsRef.current = [];
    visibleDataPointsRef.current = [];
    setTrades([]);
    tradesRef.current = [];
    currentTimeRef.current = 0;
    highlightedAreaSystemRef.current = new HighlightedAreaSystem();

    // Clear last round-scoped upgrades and show picker for the next round
    upgradesEngineRef.current.clearRoundScopedUpgrades();
    // Show upgrade picker between rounds (future extensibility)
    selectedUpgradesRef.current = [];
    setOfferedUpgrades(getThreeRandomCards());
    setShowUpgradePicker(true);
    // Update daily max with the last successfully cleared round
    const delta = StorageService.updateDailyMaxRoundsIfHigher(
      roundState?.roundNumber ?? 1
    );
    if (delta > 0) setSessionGemsEarned((prev) => prev + delta);

    // gameplay will resume after upgrade pick
  }, []);

  const handleGameEnd = useCallback(async () => {
    try {
      // Auto-sell if still holding position
      let finalMoney = money;
      if (isHolding && coinCount > 0) {
        const previousMoney = moneyRef.current;
        finalMoney = coinCount * currentPrice;
        setMoney(finalMoney);
        setCoinCount(0);
        setBuyPrice(0);
        setIsHolding(false);
        setPurchasePoint(null);

        // Precision mechanics removed

        // Record final auto-sell trade
        const lastPoint =
          fullDataPointsRef.current[fullDataPointsRef.current.length - 1];
        const trade: Trade = {
          buyTime: purchasePointRef.current?.time ?? lastPoint?.time ?? 0,
          buyPrice: buyPriceRef.current,
          sellTime: lastPoint?.time ?? currentTimeRef.current,
          sellPrice: currentPrice,
        };
        setTrades((prev) => {
          const next = [...prev, trade];
          tradesRef.current = next;
          return next;
        });

        // Highlighted area effect on auto SELL at end
        const effect =
          highlightedAreaSystemRef.current.consumeEffectIfTriggered(
            currentTimeRef.current
          );
        if (effect) applyHighlightedEffect(effect);

        // Rounds progress from auto-sell during end
        if (roundState) {
          const target =
            roundsManagerRef.current.getRemainingAmount(finalMoney);
          const from = roundRemainingDisplay;
          roundRemainingTargetRef.current = target;
          animateRoundRemaining(from, target);
          if (finalMoney - previousMoney > 0) {
            setRoundFlash("gain");
            setTimeout(() => setRoundFlash(null), 500);
          } else if (finalMoney - previousMoney < 0) {
            setRoundFlash("loss");
            setTimeout(() => setRoundFlash(null), 500);
          }
        }
      }

      // Level system removed: no multipliers

      // Stop gameplay and show overview first
      setIsPlaying(false);
      setShowSessionOverview(true);
      // Evaluate round pass for overview CTA
      const passed = roundsManagerRef.current.isRoundPassed(finalMoney);
      setRoundPassed(passed);
      if (passed && roundState) {
        const delta = StorageService.updateDailyMaxRoundsIfHigher(
          roundState.roundNumber
        );
        if (delta > 0) setSessionGemsEarned((prev) => prev + delta);
      }

      /* BACKEND INTEGRATION: Will be restored in future
      // Calculate profit/loss
      const profit = money - initialMoney;

      // Update user's coin balance in the game
      if (profit !== 0) {
        await userService?.loadUser();
      }
      */
    } catch (error) {
      console.error("Failed to end game:", error);
      // Game still ends, but we should show an error about coin sync
      setIsPlaying(false);
      setGameEnded(true);
    }
  }, [
    isHolding,
    coinCount,
    currentPrice,
    money,
    initialMoney,
    convertFinalMoneyToCoins /*, userService */,
    roundState,
    animateRoundRemaining,
  ]); // Commented out userService dependency

  const handleExitTrading = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      // End the session like time ran out: auto-sell if needed, compute stats,
      // stop gameplay and show the overview, then user can proceed to results
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      handleGameEnd();
    },
    [handleGameEnd]
  );

  const handleExitToMenu = useCallback(() => {
    // Leave the game to main menu
    setIsPlaying(false);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    navigate("/menu");
  }, [navigate]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !gameStarted) return; // Only start when game is actually started

    // Set canvas size
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    updateCanvasSize();
    window.addEventListener("resize", updateCanvasSize);

    // Start animation only when game has started
    let dataPoints: StockPoint[] = visibleDataPointsRef.current.slice();
    let currentTime = currentTimeRef.current || 0; // milliseconds since animation start
    let currentPriceValue = currentPriceRef.current || chartConfig.startPrice;
    let previousTimestamp = 0;
    let pointAccumulatorMs = 0;
    let debugAccumulatorMs = 0;
    const pointIntervalMs = 1000 / chartConfig.pointsPerSecond;

    const animate = (timestamp: number) => {
      if (!previousTimestamp) previousTimestamp = timestamp;
      const deltaMs = timestamp - previousTimestamp;
      previousTimestamp = timestamp;

      // Skip animation if game is paused
      if (isPausedRef.current) {
        if (isPlaying) {
          animationRef.current = requestAnimationFrame(animate);
        }
        return;
      }

      // Scale time progression by configured animation speed (60 => 1x)
      const speedScale = Math.max(0.1, chartConfig.animationSpeed / 60);
      const scaledDeltaMs = deltaMs * speedScale;

      currentTime += scaledDeltaMs;
      currentTimeRef.current = currentTime;

      // Update highlighted area lifecycle
      // highlightedAreaSystemRef.current.update(currentTime, scaledDeltaMs);

      // Accumulate time for point generation at consistent rate
      pointAccumulatorMs += scaledDeltaMs;
      while (pointAccumulatorMs >= pointIntervalMs) {
        currentPriceValue = generateNextPrice(currentPriceValue);
        setCurrentPrice(currentPriceValue);

        // Refresh feed info (for real mode timeframe updates)
        if (priceSourceRef.current) {
          roundFeedInfoRef.current = priceSourceRef.current.getInfo();
        }

        const maxDataPoints =
          chartConfig.pointsPerSecond * chartConfig.visibleTimeSeconds;
        const progressPercent = Math.min(
          (dataPoints.length / maxDataPoints) * 100,
          100
        );

        const newPoint: StockPoint = {
          x: progressPercent,
          y: 0,
          price: currentPriceValue,
          time: currentTime,
        };

        dataPoints.push(newPoint);
        fullDataPointsRef.current.push(newPoint);

        // Precision mechanics removed

        // Update purchase point if we just started holding and haven't set coordinates yet
        if (
          isHoldingRef.current &&
          purchasePointRef.current &&
          purchasePointRef.current.time === -1
        ) {
          setPurchasePoint({
            x: progressPercent,
            y: 0,
            time: currentTime,
          });
        }

        // Keep only visible points (configurable time window)
        if (dataPoints.length > maxDataPoints) {
          dataPoints = dataPoints.slice(-maxDataPoints);
          // Shift all x positions to keep the chart moving
          dataPoints.forEach((point, index) => {
            point.x = (index / (dataPoints.length - 1)) * 100;
          });
        }
        // Persist visible data points across effect reruns
        visibleDataPointsRef.current = dataPoints;

        pointAccumulatorMs -= pointIntervalMs;
      }

      // Level system removed: no XP tracking or time bonuses

      drawChart(
        canvas,
        dataPoints,
        isHoldingRef.current,
        currentPriceValue,
        buyPriceRef.current,
        purchasePointRef.current,
        priceMode === "pvp"
          ? {
              isHolding: botIsHoldingRef.current,
              buyPrice: botBuyPriceRef.current,
              purchasePoint: botPurchasePointRef.current,
            }
          : undefined
      );

      // Draw highlighted overlay on top (translucent)
      // const ctx2 = canvas.getContext("2d");
      // if (ctx2 && dataPoints.length > 1) {
      //   const padding = 60;
      //   const chartWidth = canvas.width - padding * 2;
      //   const chartHeight = canvas.height - padding * 2;
      //   const visibleStartTime = dataPoints[0].time;
      //   const visibleEndTime = dataPoints[dataPoints.length - 1].time;
      //   highlightedAreaSystemRef.current.drawOverlay(
      //     ctx2,
      //     canvas.width,
      //     canvas.height,
      //     padding,
      //     chartWidth,
      //     chartHeight,
      //     visibleStartTime,
      //     visibleEndTime
      //   );
      // }

      // Update and draw bubble system
      const ctx = canvas.getContext("2d");
      if (ctx) {
        // Update bubble system
        bubbleSystemRef.current.update(canvas.width, canvas.height);

        // Tap-to-collect: no line collision checks

        // Draw bubbles
        bubbleSystemRef.current.draw(ctx);

        // Draw reward animations using ref to avoid effect dependency
        const currentPerfTime = performance.now();
        rewardAnimationsRef.current.forEach((animation) => {
          const elapsed = currentPerfTime - animation.startTime;
          const progress = Math.min(elapsed / 2000, 1); // 2 second duration

          // Animation effects
          const opacity = Math.max(0, 1 - progress);
          const scale = 1 + progress * 0.5; // Grow while fading
          const yOffset = -progress * 50; // Float upward

          ctx.save();
          ctx.globalAlpha = opacity;

          // Draw reward text
          ctx.font = `bold ${16 * scale}px monospace`;
          ctx.fillStyle = "#00ff88";
          ctx.strokeStyle = "#000";
          ctx.lineWidth = 2;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          const x = animation.x;
          const y = animation.y + yOffset;

          // Stroke text for better visibility
          ctx.strokeText(`+$${animation.value}`, x, y);
          ctx.fillText(`+$${animation.value}`, x, y);

          ctx.restore();
        });

        // Precision overlays removed
      }

      // Continue animating only during active play; pause on overview
      if (isPlaying && !showOverviewRef.current) {
        // Upgrades: auto-liquidate check
        if (
          isHoldingRef.current &&
          autoLiqPctRef.current != null &&
          buyPriceRef.current > 0
        ) {
          const liqPrice =
            buyPriceRef.current * (1 - autoLiqPctRef.current / 100);
          if (currentPriceValue <= liqPrice) {
            handleSell();
          }
        }

        // PvP bot decision loop
        if (priceMode === "pvp" && botRef.current) {
          const decision = botRef.current.update(
            currentTime,
            dataPoints.map((p) => ({ time: p.time, price: p.price })),
            botIsHoldingRef.current,
            botBuyPriceRef.current,
            botBuysLeftRef.current,
            botPurchasePointRef.current?.time
          );
          if (decision === "buy") {
            if (
              !botIsHoldingRef.current &&
              botMoneyRef.current > 0 &&
              botBuysLeftRef.current > 0
            ) {
              const coins = botMoneyRef.current / currentPriceValue;
              setBotCoinCount(coins);
              setBotBuyPrice(currentPriceValue);
              setBotIsHolding(true);
              setBotBuysLeft((prev) => prev - 1);
              setBotPurchasePoint({
                x: dataPoints[dataPoints.length - 1].x,
                y: 0,
                time: currentTime,
              });
              botUpgradesRef.current.onTradeOpen();
              botLeverageMultRef.current =
                botUpgradesRef.current.getCurrentTradeLeverageMult();
              botAutoLiqPctRef.current =
                botUpgradesRef.current.getCurrentTradeAutoLiqPct();
            }
          } else if (decision === "sell") {
            if (botIsHoldingRef.current && botCoinCountRef.current > 0) {
              const sellValue = botCoinCountRef.current * currentPriceValue;
              const originalInvestment =
                botCoinCountRef.current * (botBuyPriceRef.current || 1);
              const basePct =
                ((currentPriceValue - (botBuyPriceRef.current || 0)) /
                  Math.max(1e-9, botBuyPriceRef.current || 1)) *
                100 *
                (botLeverageMultRef.current || 1);
              const holdMs = Math.max(
                0,
                currentTime - (botPurchasePointRef.current?.time ?? currentTime)
              );
              const isWin = basePct >= 0;
              const input = {
                baseProfitPct: isWin ? basePct : undefined,
                baseLossPct: !isWin ? Math.abs(basePct) : undefined,
                holdMs,
                currentFees: { buy: 0, sell: 0 },
              } as const;
              const out = isWin
                ? botUpgradesRef.current.onTradeCloseWin(input)
                : botUpgradesRef.current.onTradeCloseLoss(input);
              const effectivePct = isWin
                ? out.profitPct ?? basePct
                : -1 * (out.lossPct ?? Math.abs(basePct));
              const pnlDollar = (effectivePct / 100) * originalInvestment;
              const feeBuy = out.fees?.buy ?? 0;
              const feeSell = out.fees?.sell ?? 0;
              const feeDollar =
                feeBuy * originalInvestment + feeSell * sellValue;
              const prev = botMoneyRef.current;
              const next = Math.max(0, prev + pnlDollar - feeDollar);
              setBotMoney(next);
              setBotCoinCount(0);
              setBotBuyPrice(0);
              setBotIsHolding(false);
              setBotPurchasePoint(null);
            }
          }
          if (
            botIsHoldingRef.current &&
            botAutoLiqPctRef.current != null &&
            botBuyPriceRef.current > 0
          ) {
            const liqPriceBot =
              botBuyPriceRef.current * (1 - botAutoLiqPctRef.current / 100);
            if (currentPriceValue <= liqPriceBot) {
              // force bot sell
              const sellValue = botCoinCountRef.current * currentPriceValue;
              const originalInvestment =
                botCoinCountRef.current * (botBuyPriceRef.current || 1);
              const basePct =
                ((currentPriceValue - (botBuyPriceRef.current || 0)) /
                  Math.max(1e-9, botBuyPriceRef.current || 1)) *
                100 *
                (botLeverageMultRef.current || 1);
              const holdMs = Math.max(
                0,
                currentTime - (botPurchasePointRef.current?.time ?? currentTime)
              );
              const isWin = basePct >= 0;
              const input = {
                baseProfitPct: isWin ? basePct : undefined,
                baseLossPct: !isWin ? Math.abs(basePct) : undefined,
                holdMs,
                currentFees: { buy: 0, sell: 0 },
              } as const;
              const out = isWin
                ? botUpgradesRef.current.onTradeCloseWin(input)
                : botUpgradesRef.current.onTradeCloseLoss(input);
              const effectivePct = isWin
                ? out.profitPct ?? basePct
                : -1 * (out.lossPct ?? Math.abs(basePct));
              const pnlDollar = (effectivePct / 100) * originalInvestment;
              const feeBuy = out.fees?.buy ?? 0;
              const feeSell = out.fees?.sell ?? 0;
              const feeDollar =
                feeBuy * originalInvestment + feeSell * sellValue;
              const prev = botMoneyRef.current;
              const next = Math.max(0, prev + pnlDollar - feeDollar);
              setBotMoney(next);
              setBotCoinCount(0);
              setBotBuyPrice(0);
              setBotIsHolding(false);
              setBotPurchasePoint(null);
            }
          }
        }
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", updateCanvasSize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [drawChart, generateNextPrice, isPlaying, gameStarted]);

  const drawOverview = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Fit to window
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const dataPoints = fullDataPointsRef.current;
    const padding = 60;
    const chartWidth = canvas.width - padding * 2;
    const chartHeight = canvas.height - padding * 2;

    let minPrice = 0;
    let maxPrice = 0;
    let priceRange = 0;
    let minTime = 0;
    let maxTime = 1;
    let timeRange = 1;

    if (dataPoints.length >= 2) {
      const prices = dataPoints.map((p) => p.price);
      minPrice = Math.min(...prices) * 0.95;
      maxPrice = Math.max(...prices) * 1.05;
      priceRange = maxPrice - minPrice;

      minTime = dataPoints[0].time;
      maxTime = dataPoints[dataPoints.length - 1].time;
      timeRange = Math.max(1, maxTime - minTime);
    } else {
      // Fallback values to still render header and axes
      const lastPrice = currentPriceRef.current || 100;
      minPrice = lastPrice * 0.95;
      maxPrice = lastPrice * 1.05;
      priceRange = maxPrice - minPrice;
      minTime = 0;
      maxTime = 1;
      timeRange = 1;
    }

    // Background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Grid
    ctx.strokeStyle = "#333";
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);
    for (let i = 0; i <= 5; i++) {
      const y = padding + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(canvas.width - padding, y);
      ctx.stroke();
      const price = maxPrice - (priceRange / 5) * i;
      ctx.fillStyle = "#888";
      ctx.font = "12px monospace";
      ctx.textAlign = "right";
      ctx.fillText(`$${price.toFixed(2)}`, padding - 10, y + 4);
    }
    ctx.setLineDash([]);

    // Axes
    ctx.strokeStyle = "#555";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, canvas.height - padding);
    ctx.moveTo(padding, canvas.height - padding);
    ctx.lineTo(canvas.width - padding, canvas.height - padding);
    ctx.stroke();

    // Price line
    if (dataPoints.length >= 2) {
      ctx.strokeStyle = "#ffdd44";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      dataPoints.forEach((point, index) => {
        const t = (point.time - minTime) / timeRange;
        const x = padding + t * chartWidth;
        const y =
          padding +
          chartHeight -
          ((point.price - minPrice) / priceRange) * chartHeight;
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }

    // Trades
    if (dataPoints.length >= 2) {
      tradesRef.current.forEach((trade) => {
        const buyX =
          padding + ((trade.buyTime - minTime) / timeRange) * chartWidth;
        const sellX =
          padding + ((trade.sellTime - minTime) / timeRange) * chartWidth;
        const buyY =
          padding +
          chartHeight -
          ((trade.buyPrice - minPrice) / priceRange) * chartHeight;
        const sellY =
          padding +
          chartHeight -
          ((trade.sellPrice - minPrice) / priceRange) * chartHeight;

        const isProfit = trade.sellPrice - trade.buyPrice >= 0;
        ctx.strokeStyle = isProfit ? "#00ff88" : "#ff4444";
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 6]);
        ctx.beginPath();
        ctx.moveTo(buyX, buyY);
        ctx.lineTo(sellX, sellY);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = ctx.strokeStyle;
        ctx.beginPath();
        ctx.arc(buyX, buyY, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(sellX, sellY, 4, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    // Removed captions for results screen to avoid overlay conflicts on mobile
  }, []);

  useEffect(() => {
    if (!showSessionOverview) return;
    // Draw immediately when toggled on
    drawOverview();
    const onResize = () => drawOverview();
    window.addEventListener("resize", onResize);
    // Also schedule a follow-up draw on next frame to ensure canvas is visible
    const raf = requestAnimationFrame(() => drawOverview());
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, [showSessionOverview, drawOverview]);

  // Timer effect
  useEffect(() => {
    if (!isPlaying || gameEnded || isPaused) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          // Auto-sell if still holding position when time runs out
          let finalMoney = moneyRef.current;
          if (isHoldingRef.current && coinCountRef.current > 0) {
            // Resolve trade close through upgrades engine on timeout as loss or win based on price
            const basePct =
              ((currentPriceRef.current - buyPriceRef.current) /
                Math.max(1e-9, buyPriceRef.current)) *
              100 *
              (leverageMultRef.current || 1);
            const holdMs = Math.max(
              0,
              currentTimeRef.current -
                (purchasePointRef.current?.time ?? currentTimeRef.current)
            );
            const input = {
              baseProfitPct: basePct >= 0 ? basePct : undefined,
              baseLossPct: basePct < 0 ? Math.abs(basePct) : undefined,
              holdMs,
              currentFees: { buy: 0, sell: 0 },
            } as const;
            const out =
              basePct >= 0
                ? upgradesEngineRef.current.onTradeCloseWin(input)
                : upgradesEngineRef.current.onTradeCloseLoss(input);
            const effectivePct =
              basePct >= 0
                ? out.profitPct ?? basePct
                : -1 * (out.lossPct ?? Math.abs(basePct));
            const originalInvestment =
              coinCountRef.current * buyPriceRef.current;
            const pnlDollar = (effectivePct / 100) * originalInvestment;
            const feeBuy = out.fees?.buy ?? 0;
            const feeSell = out.fees?.sell ?? 0;
            const sellValue = coinCountRef.current * currentPriceRef.current;
            const feeDollar = feeBuy * originalInvestment + feeSell * sellValue;
            finalMoney = Math.max(0, moneyRef.current + pnlDollar - feeDollar);
            setMoney(finalMoney);
            setCoinCount(0);
            setBuyPrice(0);
            setIsHolding(false);
            setPurchasePoint(null);

            // Precision mechanics removed

            // Record auto-sell trade
            const lastPoint =
              fullDataPointsRef.current[fullDataPointsRef.current.length - 1];
            const autoTrade: Trade = {
              buyTime: purchasePointRef.current?.time ?? lastPoint?.time ?? 0,
              buyPrice: buyPriceRef.current,
              sellTime: lastPoint?.time ?? currentTimeRef.current,
              sellPrice: currentPriceRef.current,
            };
            setTrades((prevTrades) => {
              const next = [...prevTrades, autoTrade];
              tradesRef.current = next;
              return next;
            });
          }

          // Level system removed: end immediately

          // Stop gameplay and show overview first
          setIsPlaying(false);
          setShowSessionOverview(true);
          // Evaluate round result for overview action
          const passed = roundsManagerRef.current.isRoundPassed(finalMoney);
          setRoundPassed(passed);
          if (passed && roundState) {
            StorageService.updateDailyMaxRoundsIfHigher(roundState.roundNumber);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPlaying, gameEnded, isPaused, initialMoney]); // Use refs for current values

  // Synchronize round goal when realized cash changes (e.g., after SELL)
  useEffect(() => {
    if (!roundState) return;
    if (isHolding) return; // Only update on realized PnL
    const target = roundsManagerRef.current.getRemainingAmount(money);
    const from = roundRemainingDisplay;
    if (target !== roundRemainingTargetRef.current) {
      roundRemainingTargetRef.current = target;
      animateRoundRemaining(from, target);
    }
  }, [
    money,
    isHolding,
    roundState,
    animateRoundRemaining,
    roundRemainingDisplay,
  ]);

  // Calculate profit/loss when holding
  const currentValue = isHolding ? coinCount * currentPrice : money;
  const profitLoss = isHolding ? currentValue - money : 0;
  const profitLossPercent =
    isHolding && buyPrice > 0
      ? ((currentPrice - buyPrice) / buyPrice) * 100
      : 0;

  return (
    <div className="simple-game">
      {!gameStarted && (
        <PreGameModal
          onStartGame={handleStartGame}
          onCancel={handleCancelPreGame}
        />
      )}

      {gameStarted && showUpgradePicker && (
        <UpgradePickerModal
          upgrades={offeredUpgrades}
          onPick={(u) => {
            // For now: pick single upgrade; can extend to pick multiple sequentially
            if (u.scope === "next_round")
              upgradesEngineRef.current.offerNextRoundUpgrade(u);
            else upgradesEngineRef.current.addUpgrade(u);

            // Start or continue round after pick
            const currentCash = moneyRef.current;
            const rs = roundState
              ? roundsManagerRef.current.startNextRound(currentCash)
              : roundsManagerRef.current.startFirstRound(currentCash);
            setRoundState(rs);
            roundNumberRef.current = rs.roundNumber;
            roundRemainingTargetRef.current = Math.round(rs.requiredAmount);
            setRoundRemainingDisplay(Math.round(rs.requiredAmount));
            setRoundPassed(false);

            // Initialize price source for the round
            priceSourceRef.current = createPriceSource(priceMode, {
              chartConfig,
              approximateNeededPoints:
                chartConfig.pointsPerSecond * chartConfig.visibleTimeSeconds +
                60,
            });
            roundFeedInfoRef.current = priceSourceRef.current.getInfo();

            // PvP: apply round_start upgrades to bot as well
            if (priceMode === "pvp") {
              const out = botUpgradesRef.current.onRoundStart(
                rs.roundNumber,
                rs.roundStartMoney
              );
              if (out.addTimeSec) setTimeLeft((prev) => prev + out.addTimeSec);
              if (out.addBuys) setBotBuysLeft((prev) => prev + out.addBuys);
            }

            // Apply round_start effects
            const startOut = upgradesEngineRef.current.onRoundStart(
              rs.roundNumber,
              rs.roundStartMoney
            );
            if (startOut.addTimeSec)
              setTimeLeft((prev) => prev + startOut.addTimeSec);
            if (startOut.addBuys)
              setBuysLeft((prev) => prev + startOut.addBuys);
            // lives not implemented in this simple game; reserved for future

            setShowUpgradePicker(false);
            setIsPlaying(true);
          }}
          onCancel={() => {
            // If user cancels, just start without extra upgrade
            const currentCash = moneyRef.current;
            const rs = roundState
              ? roundsManagerRef.current.startNextRound(currentCash)
              : roundsManagerRef.current.startFirstRound(currentCash);
            setRoundState(rs);
            roundNumberRef.current = rs.roundNumber;
            roundRemainingTargetRef.current = Math.round(rs.requiredAmount);
            setRoundRemainingDisplay(Math.round(rs.requiredAmount));
            setRoundPassed(false);

            const startOut = upgradesEngineRef.current.onRoundStart(
              rs.roundNumber,
              rs.roundStartMoney
            );
            if (startOut.addTimeSec)
              setTimeLeft((prev) => prev + startOut.addTimeSec);
            if (startOut.addBuys)
              setBuysLeft((prev) => prev + startOut.addBuys);
            // Initialize price source for the round
            priceSourceRef.current = createPriceSource(priceMode, {
              chartConfig,
              approximateNeededPoints:
                chartConfig.pointsPerSecond * chartConfig.visibleTimeSeconds +
                60,
            });
            roundFeedInfoRef.current = priceSourceRef.current.getInfo();
            setShowUpgradePicker(false);
            setIsPlaying(true);
          }}
        />
      )}

      {gameStarted && (
        <>
          <canvas
            ref={canvasRef}
            className="stock-chart-canvas"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onContextMenu={handleContextMenu}
            style={{
              touchAction: "none",
              cursor: showSessionOverview
                ? "default"
                : isHolding
                ? "pointer"
                : "crosshair",
            }}
          />
          <div className="game-ui">
            {/* Removed floating coin-badge; we will display coin name inside wallet widget */}
            {showSessionOverview && !gameEnded && (
              <div
                style={{
                  position: "fixed",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  justifyContent: "center",
                  padding: "16px",
                  zIndex: 5,
                }}
              >
                {roundPassed ? (
                  <Button
                    size="l"
                    onClick={handleProceedToNextRound}
                    className="exit-button"
                  >
                    Next round
                  </Button>
                ) : (
                  <Button
                    size="l"
                    onClick={handleProceedToEnd}
                    className="exit-button"
                  >
                    Continue
                  </Button>
                )}
              </div>
            )}
            <div
              className={`trading-info ${
                showSessionOverview ? "trading-info--overview" : ""
              }`}
            >
              {showSessionOverview && (
                <div className="results-info-card">
                  {roundFeedInfoRef.current?.mode !== "random" && (
                    <>
                      <div style={{ color: "#fff" }}>
                        {roundFeedInfoRef.current?.coinLabel}
                      </div>
                      <div style={{ color: "#bbb", marginTop: 2 }}>
                        {formatDateTimeFriendly(
                          roundFeedInfoRef.current?.startIso
                        )}
                        {" → "}
                        {formatDateTimeFriendly(
                          roundFeedInfoRef.current?.endIso
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="wallet-info">
                <div className="wallet-label">
                  {isHolding
                    ? "HOLDINGS"
                    : (roundFeedInfoRef.current &&
                        roundFeedInfoRef.current.mode !== "random" &&
                        roundFeedInfoRef.current.coinLabel) ||
                      "CASH"}
                </div>
                <div className="wallet-amount">
                  {isHolding
                    ? `${coinCount.toFixed(4)} ${
                        roundFeedInfoRef.current &&
                        roundFeedInfoRef.current.mode !== "random" &&
                        roundFeedInfoRef.current.coinLabel
                          ? roundFeedInfoRef.current.coinLabel
                          : "MEME"
                      }`
                    : `$${money.toFixed(2)}`}
                </div>
                {/* Redundant coin-inline removed; coin shows in the label above */}
                {isHolding && (
                  <div
                    className={`profit-loss ${
                      profitLoss >= 0 ? "profit" : "loss"
                    }`}
                  >
                    {profitLoss >= 0 ? "+" : ""}${profitLoss.toFixed(2)} (
                    {profitLossPercent >= 0 ? "+" : ""}
                    {profitLossPercent.toFixed(1)}%)
                  </div>
                )}
                {priceMode === "pvp" && (
                  <div
                    style={{
                      marginTop: 6,
                      color: "#4da3ff",
                      fontSize: 20,
                      fontWeight: "bold",
                      textShadow: "0 0 10px rgba(77,163,255,0.5)",
                      letterSpacing: 1,
                    }}
                  >
                    PvP ${botMoney.toFixed(2)}
                  </div>
                )}
              </div>

              {roundState && (
                <div
                  className={`round-progress ${
                    roundFlash === "gain"
                      ? "round-progress--gain"
                      : roundFlash === "loss"
                      ? "round-progress--loss"
                      : ""
                  }`}
                >
                  <div className="round-title">
                    ROUND {roundState.roundNumber}
                  </div>
                  <div className="round-remaining">
                    ${roundRemainingDisplay}
                  </div>
                  <div className="round-subtitle">to clear this round</div>
                </div>
              )}

              {/* Level system UI removed */}

              {!showSessionOverview && (
                <div className="timer-info">
                  <div className="timer-label">TIME LEFT</div>
                  <div
                    className={`timer-value ${timeLeft <= 10 ? "warning" : ""}`}
                  >
                    {Math.floor(timeLeft / 60)}:
                    {(timeLeft % 60).toString().padStart(2, "0")}
                  </div>
                </div>
              )}
            </div>

            {!showSessionOverview && (
              <div className="trading-controls">
                <div className="trading-instructions">
                  <div className="instruction-text">
                    {isHolding ? (
                      "🟢 HOLDING - Release to SELL"
                    ) : buysLeft > 0 ? (
                      <>
                        Tap & Hold to BUY (
                        <span className="buys-left-highlight">{buysLeft}</span>{" "}
                        left) • Release to SELL
                      </>
                    ) : (
                      "No buys left - Game continues until time runs out"
                    )}
                  </div>
                </div>

                <Button
                  size="l"
                  onClick={handleExitTrading}
                  className="exit-button"
                >
                  Exit Trading
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {gameStarted && isPlaying && !showSessionOverview && (
        <NewsTicker
          enabled={isPlaying}
          priceSource={priceSourceRef.current}
          pointsPerSecond={chartConfig.pointsPerSecond}
          currentPrice={currentPrice}
          mode={priceMode}
        />
      )}

      {gameEnded && !showSessionOverview && (
        <EndGameScreen
          initialMoney={initialMoney}
          finalMoney={money}
          sessionGems={sessionGemsEarned}
          onPlayAgain={handlePlayAgain}
          onExit={handleExitToMenu}
        />
      )}

      {/* Level up modal removed */}
    </div>
  );
}
